import styled, { css } from 'styled-components';

import * as LayoutUtils from './utils';
import type { ButtonsContainer, LayoutPanels, BaseContainer } from './utils';
import { txt } from '../../../../locales/i18n';

export const buttonLayoutPanels: LayoutPanels = {
	layoutName: txt('cms.layoutNames.buttons'),
	panels: [
		{
			label: txt('cms.panelNames.layout container'),
			formKey: 'container',
			type: 'baseContainerPanel',
		},

		{
			label: txt('cms.panelNames.button container'),
			formKey: 'buttonContainer',
			type: 'buttonsContainerPanel',
		},
	],
};

export const buttonLayoutValues: ButtonLayoutProps = {
	container: LayoutUtils.defaultBaseContainer,
	buttonContainer: {
		container: LayoutUtils.defaultButtonsContainer.container,
		primary: { ...LayoutUtils.defaultButtonsContainer.primary, visible: true },
		secondary: { ...LayoutUtils.defaultButtonsContainer.secondary, visible: true },
		animation: LayoutUtils.defaultAnimation,
	},
};

type ButtonLayoutProps = {
	container: BaseContainer;
	buttonContainer: ButtonsContainer;
};

export const ButtonLayout = (props: ButtonLayoutProps) => {
	return (
		<ButtonLayoutPreviewStyled
			styledProps={props}
			{...LayoutUtils.getAnimationProps(props.container.animation)}
		>
			<div
				className="button-container"
				{...LayoutUtils.getAnimationProps(props.buttonContainer.animation)}
			>
				{props.buttonContainer.primary.visible && (
					<button className="primary">{props.buttonContainer.primary.text}</button>
				)}

				{props.buttonContainer.secondary.visible && (
					<button className="secondary">{props.buttonContainer.secondary.text}</button>
				)}
			</div>
		</ButtonLayoutPreviewStyled>
	);
};

const ButtonLayoutPreviewStyled = styled.div<{ styledProps: ButtonLayoutProps }>`
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 3em;
	background-color: ${({ styledProps }) => styledProps.container.backgroundColor};
	min-height: ${({ styledProps }) => (styledProps.container.fullscreen ? '100dvh' : 'auto')};

	align-items: ${({ styledProps }) => {
		if (styledProps.buttonContainer.container.placement === 'left') return 'flex-start';
		if (styledProps.buttonContainer.container.placement === 'right') return 'flex-end';
		return 'center';
	}};

	.button-container {
		display: flex;
		gap: 2ch;
	}

	background-repeat: no-repeat;
	background-color: ${({ styledProps }) => styledProps.container.backgroundColor};
	background-size: ${({ styledProps }) => styledProps?.container?.coverFit ?? 'cover'};
	background-position: ${({ styledProps }) => styledProps?.container?.coverPosition ?? 'center'};
	background-image: ${({ styledProps }) => {
		if (styledProps.container.coverSource === '') return 'none';
		return `url(${LayoutUtils.getImageSource(styledProps.container.coverSource)})`;
	}};

	button {
		padding: 0.8em 1.6em;
		border-radius: 2em;
		font-family: Raleway Variable;
	}

	button.primary {
		color: ${({ styledProps }) => styledProps.buttonContainer.primary.textColor};

		background-color: ${({ styledProps }) => {
			if (styledProps.buttonContainer.primary.design === 'default') {
				return styledProps.buttonContainer.primary.backgroundColor;
			}

			return 'transparent';
		}};

		border: ${({ styledProps }) => {
			if (styledProps.buttonContainer.primary.design === 'simple') {
				return '4px solid ' + styledProps.buttonContainer.primary.backgroundColor;
			}

			return 'none';
		}};
	}

	button.secondary {
		color: ${({ styledProps }) => styledProps.buttonContainer.secondary.textColor};

		background-color: ${({ styledProps }) => {
			if (styledProps.buttonContainer.secondary.design === 'default') {
				return styledProps.buttonContainer.secondary.backgroundColor;
			}

			return 'transparent';
		}};

		border: ${({ styledProps }) => {
			if (styledProps.buttonContainer.secondary.design === 'simple') {
				return '4px solid ' + styledProps.buttonContainer.secondary.backgroundColor;
			}

			return 'none';
		}};
	}
`;
